import React, { useState } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import { useAlert } from '../../../../../../context/AlertContext';
import stockImportServices from '../stockImportServices';

function StockImportPage() {
    const [file, setFile] = useState(null);
    const [saving, setSavingStatus] = useState(false);
    const [responseMessage, setResponseMessage] = useState(null);
    const [error, setError] = useState("");
    const [jsonData, setJsonData] = useState(null);
    
    // Extracted Data States
    const [extractedProducts, setExtractedProducts] = useState(null);
    const [extractedUnits, setExtractedUnits] = useState(null);
    const [extractedStockGroups, setExtractedStockGroups] = useState(null);
    const [extractedGodowns, setExtractedGodowns] = useState(null);
    const [extractedTaxUnits, setExtractedTaxUnits] = useState(null); // Added TaxUnits state
    
    const [isLoading, setIsLoading] = useState(false);

    // Upload Step Tracking States (5 Parts)
    const [unitUploadStatus, setUnitUploadStatus] = useState('idle');       // 'idle' | 'uploading' | 'success' | 'error'
    const [godownUploadStatus, setGodownUploadStatus] = useState('idle');   // 'idle' | 'uploading' | 'success' | 'error'
    const [groupUploadStatus, setGroupUploadStatus] = useState('idle');     // 'idle' | 'uploading' | 'success' | 'error'
    const [taxUnitUploadStatus, setTaxUnitUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'
    const [productUploadStatus, setProductUploadStatus] = useState('idle'); // 'idle' | 'uploading' | 'success' | 'error'

    // Progress tracking states for chunk uploading products
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [progressPercentage, setProgressPercentage] = useState(0);

    const { AppAlert } = useAlert();

    // Helper to clean Tally control characters like \u0004
    const cleanTallyText = (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\\u0004|\u0004/g, '').trim();
    };

    // Unified extraction function for Stock Items, Units, Stock Groups, Godowns, and TaxUnits
    const extractTallyData = (rawObj) => {
        if (!rawObj || !rawObj.tallymessage) return { stockItems: [], units: [], stockGroups: [], godowns: [], taxUnits: [] };

        const stockItems = [];
        const units = [];
        const stockGroups = [];
        const godowns = [];
        const taxUnits = [];

        rawObj.tallymessage.forEach(item => {
            if (!item.metadata || !item.metadata.type) return;

            const type = item.metadata.type;
            const name = cleanTallyText(item.metadata.name) || "";

            if (type === "Stock Item") {
                const validHsnDetail = item.hsndetails?.find(h => h.hsncode) || item.hsndetails?.[item.hsndetails.length - 1];
                
                let gstRate = "0";
                let taxability = "Unknown";

                if (item.gstdetails && Array.isArray(item.gstdetails)) {
                    const gstDetailsList = [...item.gstdetails].reverse();
                    const activeGstDetail = gstDetailsList.find(g => g.taxability && g.statewisedetails) || item.gstdetails[0];
                    
                    if (activeGstDetail) {
                        taxability = cleanTallyText(activeGstDetail.taxability) || "Unknown";
                        
                        for (const stateDetail of (activeGstDetail.statewisedetails || [])) {
                            const igstRateObj = stateDetail.ratedetails?.find(r => r.gstratedutyhead === "IGST" && r.gstrate);
                            if (igstRateObj) {
                                const cleanRate = igstRateObj.gstrate.trim();
                                gstRate = `${cleanRate}`;
                                break;
                            }
                        }
                    }
                }

                stockItems.push({
                    title: name,
                    category: cleanTallyText(item.parent) || "Not Applicable",
                    hsncode: cleanTallyText(validHsnDetail?.hsncode) || "",
                    typeofsupply: cleanTallyText(item.gsttypeofsupply) || "",
                    gstRate: gstRate,
                    taxability: taxability,
                    baseunit: cleanTallyText(item.baseunits) || "",
                    batchallocations: item.batchallocations || []
                });
            } else if (type === "Unit") {
                if (name && !units.includes(name)) {
                    units.push(name);
                }
            } else if (type === "Stock Group") {
                if (name && !stockGroups.includes(name)) {
                    stockGroups.push(name);
                }
            } else if (type === "Godown") {
                if (name && !godowns.includes(name)) {
                    godowns.push({
                        name:name,
                        taxUnitName: cleanTallyText(item.taxunitname) || "",
                    });
                }
            } else if (type === "TaxUnit") {
                // Format Address using requested logic
                let rawAddress = item.address;
                let formattedAddress = "";
                if (Array.isArray(rawAddress)) {
                    formattedAddress = rawAddress
                        .filter(line => typeof line === 'string')
                        .map(line => cleanTallyText(line))
                        .join(', ');
                } else if (typeof rawAddress === 'string') {
                    formattedAddress = cleanTallyText(rawAddress);
                }

                taxUnits.push({
                    name: name,
                    locationName: cleanTallyText(item.excisemailingname) || "",
                    address: formattedAddress,
                    state: cleanTallyText(item.priorstatename) || "",
                    zip: cleanTallyText(item.pincode) || "",
                    city: "" // Tally JSON structure typically embeds city within address lines or state; adjust if a specific key exists
                });
            }
        });

        return { stockItems, units, stockGroups, godowns, taxUnits };
    };

    // Handle file selection and parsing
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        setFile(selectedFile); 
        setResponseMessage(null);
        setError("");
        setJsonData(null);
        setExtractedProducts(null);
        setExtractedUnits(null);
        setExtractedStockGroups(null);
        setExtractedGodowns(null);
        setExtractedTaxUnits(null);

        // Reset all upload statuses
        setUnitUploadStatus('idle');
        setGodownUploadStatus('idle');
        setGroupUploadStatus('idle');
        setTaxUnitUploadStatus('idle');
        setProductUploadStatus('idle');

        if (!selectedFile) return;

        if (selectedFile.type !== "application/json" && !selectedFile.name.endsWith('.json')) {
            setError('Please select a valid JSON file.');
            AppAlert('Please select a valid JSON file.', 'error');
            return;
        }

        setIsLoading(true);
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const parsedData = JSON.parse(event.target.result);
                setJsonData(parsedData); 

                const { stockItems, units, stockGroups, godowns, taxUnits } = extractTallyData(parsedData);
                setExtractedProducts(stockItems);
                setExtractedUnits(units);
                setExtractedStockGroups(stockGroups);
                setExtractedGodowns(godowns);
                setExtractedTaxUnits(taxUnits);
            } catch (err) {
                setError('Failed to parse JSON. Please check file formatting.');
                AppAlert('Failed to parse JSON.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        reader.onerror = () => {
            setError('Error reading the file.');
            setIsLoading(false);
        };

        reader.readAsText(selectedFile);
    };

    // Sequential Upload Script including TaxUnits
    const handleUploadToServer = async () => {
        if (!extractedProducts && !extractedUnits && !extractedStockGroups && !extractedGodowns && !extractedTaxUnits) return;

        setSavingStatus(true);
        setError("");
        setResponseMessage(null);

        try {
            // PART 1: Upload Units
            if (extractedUnits && extractedUnits.length > 0) {
                setUnitUploadStatus('uploading');
                await new Promise((resolve, reject) => {
                    stockImportServices.importTallyUnits(extractedUnits)
                        .then(res => { setUnitUploadStatus('success'); resolve(res); })
                        .catch(err => { setUnitUploadStatus('error'); reject(err); });
                });
            } else {
                setUnitUploadStatus('success');
            }

            // PART 2: Upload Godowns
            if (extractedGodowns && extractedGodowns.length > 0) {
                setGodownUploadStatus('uploading');
                await new Promise((resolve, reject) => {
                    stockImportServices.importTallyGodowns({ taxUnits: extractedTaxUnits,godowns: extractedGodowns })
                        .then(res => { setGodownUploadStatus('success'); resolve(res); })
                        .catch(err => { setGodownUploadStatus('error'); reject(err); });
                });
            } else {
                setGodownUploadStatus('success');
            }

            // PART 3: Upload Stock Groups
            if (extractedStockGroups && extractedStockGroups.length > 0) {
                setGroupUploadStatus('uploading');
                await new Promise((resolve, reject) => {
                    stockImportServices.importTallyStockGroups(extractedStockGroups)
                        .then(res => { setGroupUploadStatus('success'); resolve(res); })
                        .catch(err => { setGroupUploadStatus('error'); reject(err); });
                });
            } else {
                setGroupUploadStatus('success');
            }

            // PART 4: Upload Tax Units (New Service method placeholder: importTallyTaxUnits)
            if (extractedTaxUnits && extractedTaxUnits.length > 0) {
                setTaxUnitUploadStatus('uploading');
                await new Promise((resolve, reject) => {
                    stockImportServices.importTallyTaxUnits({ taxUnits: extractedTaxUnits,godowns: extractedGodowns })
                        .then(res => { setTaxUnitUploadStatus('success'); resolve(res); })
                        .catch(err => { setTaxUnitUploadStatus('error'); reject(err); });
                });
            } else {
                setTaxUnitUploadStatus('success');
            }

            // PART 5: Upload Stock Items / Products in Chunks
            if (extractedProducts && extractedProducts.length > 0) {
                setProductUploadStatus('uploading');
                const totalItems = extractedProducts.length;
                const CHUNK_SIZE = 1000;
                let uploadedCount = 0;

                setUploadProgress({ current: 0, total: totalItems });
                setProgressPercentage(0);

                for (let i = 0; i < totalItems; i += CHUNK_SIZE) {
                    const chunk = extractedProducts.slice(i, i + CHUNK_SIZE);
                    
                    await new Promise((resolve, reject) => {
                        stockImportServices.importTallyProducts({ products: chunk })
                            .then(res => {
                                uploadedCount += chunk.length;
                                setUploadProgress({ current: uploadedCount, total: totalItems });
                                setProgressPercentage(Math.round((uploadedCount / totalItems) * 100));
                                resolve(res);
                            })
                            .catch(err => reject(err));
                    });
                }
                setProductUploadStatus('success');
            } else {
                setProductUploadStatus('success');
            }

            AppAlert("All Stock & Metadata records imported successfully!");
            setResponseMessage('Data imported successfully!');

        } catch (err) {
            setError(err?.msg || 'Upload failed');
            AppAlert(err?.msg || "Upload failed", "error");
        } finally {
            setSavingStatus(false);
        }
    };

    return (
        <FinanceLayout>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <h1>Product & Metadata Import</h1>
                        <div className="mb-3">
                            <label htmlFor="formFile" className="form-label">Upload JSON File</label>
                            <input className="form-control" type="file" id="formFile" accept=".json" onChange={handleFileChange} />
                        </div>
                        
                        {error && <div className="alert alert-danger">{error}</div>}
                        {responseMessage && <div className="alert alert-success">{responseMessage}</div>}
                        
                        <button 
                            type="button" 
                            className="btn btn-primary" 
                            onClick={handleUploadToServer}
                            disabled={saving || isLoading || !extractedProducts?.length}
                        >
                            {saving ? 'Processing Import...' : 'Start Full Import'}
                        </button>

                        {/* Step 1: Units */}
                        {unitUploadStatus !== 'idle' && (
                            <div className="mt-3 d-flex align-items-center">
                                <span className="me-2 fw-bold">1. Units:</span>
                                {unitUploadStatus === 'uploading' && <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>}
                                {unitUploadStatus === 'success' && <span className="text-success fw-bold">✓ Units Updated Successfully</span>}
                                {unitUploadStatus === 'error' && <span className="text-danger">Failed to update units</span>}
                            </div>
                        )}

                        {/* Step 2: Godowns */}
                        {godownUploadStatus !== 'idle' && (
                            <div className="mt-2 d-flex align-items-center">
                                <span className="me-2 fw-bold">2. Godowns:</span>
                                {godownUploadStatus === 'uploading' && <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>}
                                {godownUploadStatus === 'success' && <span className="text-success fw-bold">✓ Godowns Updated Successfully</span>}
                                {godownUploadStatus === 'error' && <span className="text-danger">Failed to update godowns</span>}
                            </div>
                        )}

                        {/* Step 3: Stock Groups */}
                        {groupUploadStatus !== 'idle' && (
                            <div className="mt-2 d-flex align-items-center">
                                <span className="me-2 fw-bold">3. Stock Groups:</span>
                                {groupUploadStatus === 'uploading' && <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>}
                                {groupUploadStatus === 'success' && <span className="text-success fw-bold">✓ Stock Groups Updated Successfully</span>}
                                {groupUploadStatus === 'error' && <span className="text-danger">Failed to update stock groups</span>}
                            </div>
                        )}

                        {/* Step 4: Tax Units */}
                        {taxUnitUploadStatus !== 'idle' && (
                            <div className="mt-2 d-flex align-items-center">
                                <span className="me-2 fw-bold">4. Tax Units:</span>
                                {taxUnitUploadStatus === 'uploading' && <span className="spinner-border spinner-border-sm text-primary me-2" role="status"></span>}
                                {taxUnitUploadStatus === 'success' && <span className="text-success fw-bold">✓ Tax Units Updated Successfully</span>}
                                {taxUnitUploadStatus === 'error' && <span className="text-danger">Failed to update tax units</span>}
                            </div>
                        )}

                        {/* Step 5: Stock Items & Progress */}
                        {(productUploadStatus !== 'idle' || taxUnitUploadStatus === 'success') && (
                            <div className="mt-2">
                                <div className="d-flex align-items-center mb-1">
                                    <span className="me-2 fw-bold">5. Stock Items:</span>
                                    {productUploadStatus === 'uploading' && <span className="text-muted">Uploading records in batches...</span>}
                                    {productUploadStatus === 'success' && <span className="text-success fw-bold">✓ Stock Items Uploaded Successfully</span>}
                                    {productUploadStatus === 'idle' && taxUnitUploadStatus === 'success' && <span className="text-muted">Waiting to upload...</span>}
                                </div>
                                
                                {productUploadStatus === 'uploading' && (
                                    <>
                                        <div className="d-flex justify-content-between mb-1">
                                            <small>{uploadProgress.current} / {uploadProgress.total} records</small>
                                            <small>{progressPercentage}%</small>
                                        </div>
                                        <div className="progress" style={{ height: '20px' }}>
                                            <div 
                                                className="progress-bar progress-bar-striped progress-bar-animated" 
                                                role="progressbar" 
                                                style={{ width: `${progressPercentage}%` }}
                                                aria-valuenow={progressPercentage} 
                                                aria-valuemin="0" 
                                                aria-valuemax="100"
                                            >
                                                {progressPercentage}%
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className='row'>
                    {isLoading && (
                        <div className="col-12 text-center" style={{ marginTop: '20px' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Reading and parsing JSON file...</p>
                        </div>
                    )}

                    {!isLoading && extractedProducts && (
                        <div className="col-12 mt-4">
                            <div className="row">
                                <div className="col-md-3 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Units ({extractedUnits?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedUnits, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Stock Groups ({extractedStockGroups?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedStockGroups, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Godowns ({extractedGodowns?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedGodowns, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="p-3 bg-light rounded">
                                        <h5>Tax Units ({extractedTaxUnits?.length || 0}):</h5>
                                        <pre style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {JSON.stringify(extractedTaxUnits, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>

                            <div className="p-3 bg-light rounded mt-2">
                                <h5>Extracted Stock Items Preview ({extractedProducts.length}):</h5>
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '300px', overflowY: 'auto' }}>
                                    {JSON.stringify(extractedProducts, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </FinanceLayout>
    );
}

export default StockImportPage;